/**
 * Solana raw message deserializer.
 *
 * Parses a raw serialized Solana transaction message into
 * txViz's SolanaTransactionInput type.
 *
 * Accepts 0x-prefixed hex, bare hex, or base58-encoded input.
 *
 * Handles the legacy message format. Versioned (v0) messages are
 * detected but not yet fully supported.
 */

import type { SolanaTransactionInput, SolanaInstruction, SolanaAccountMeta } from '@txviz/core';
import { hexToBytes, bytesToHex } from '@txviz/core';
import { base58Decode, base58Encode } from './types.js';

/**
 * Parse a raw serialized Solana transaction message.
 *
 * @param input - The serialized message as 0x-prefixed hex, bare hex, or base58
 * @returns A structured SolanaTransactionInput with serializedMessage set
 * @throws {Error} If the message cannot be parsed
 */
export function parseRawSolanaMessage(input: string): SolanaTransactionInput {
  const trimmed = input.trim();

  let bytes: Uint8Array;
  let serializedMessageHex: string;

  if (trimmed.startsWith('0x')) {
    bytes = hexToBytes(trimmed);
    serializedMessageHex = trimmed;
  } else if (/^[0-9a-fA-F]+$/.test(trimmed) && trimmed.length % 2 === 0 && trimmed.length > 64) {
    // Long all-hex string → treat as hex without prefix
    bytes = hexToBytes(`0x${trimmed}`);
    serializedMessageHex = `0x${trimmed}`;
  } else {
    // Assume base58
    bytes = base58Decode(trimmed);
    serializedMessageHex = bytesToHex(bytes);
  }

  const reader = new BufferReader(bytes);

  // 1. Header (3 bytes)
  const numRequiredSignatures = reader.readUint8();
  const numReadonlySignedAccounts = reader.readUint8();
  const numReadonlyUnsignedAccounts = reader.readUint8();

  // 2. Account keys
  const numAccountKeys = decodeCompactU16(reader);
  const accountKeys: string[] = [];

  for (let i = 0; i < numAccountKeys; i++) {
    const keyBytes = reader.readBytes(32);
    accountKeys.push(base58Encode(keyBytes));
  }

  // 3. Recent blockhash (32 bytes)
  const blockhashBytes = reader.readBytes(32);
  const recentBlockhash = base58Encode(blockhashBytes);

  // 4. Instructions
  const numInstructions = decodeCompactU16(reader);
  const instructions: SolanaInstruction[] = [];

  // Pre-compute the boundary for writable signers
  const numWritableSigners = numRequiredSignatures - numReadonlySignedAccounts;

  for (let i = 0; i < numInstructions; i++) {
    // Program ID index (1 byte)
    const programIdIndex = reader.readUint8();
    const programId = accountKeys[programIdIndex];
    if (programId === undefined) {
      throw new Error(`Invalid program ID index: ${programIdIndex} (${numAccountKeys} accounts total)`);
    }

    // Account indexes
    const numAccounts = decodeCompactU16(reader);
    const accounts: SolanaAccountMeta[] = [];

    for (let j = 0; j < numAccounts; j++) {
      const accountIndex = reader.readUint8();
      const pubkey = accountKeys[accountIndex];
      if (pubkey === undefined) {
        throw new Error(`Invalid account index: ${accountIndex}`);
      }

      // Reconstruct isSigner and isWritable from header counts and position.
      //
      // Account ordering (defined in serializer.ts):
      //   [0, numWritableSigners)                                        → writable signers
      //   [numWritableSigners, numRequiredSignatures)                    → read-only signers
      //   [numRequiredSignatures, numAccountKeys - numReadonlyUnsigned)  → writable non-signers
      //   [numAccountKeys - numReadonlyUnsigned, numAccountKeys)         → read-only non-signers
      const isSigner = accountIndex < numRequiredSignatures;
      const isWritable =
        accountIndex < numWritableSigners ||
        (accountIndex >= numRequiredSignatures &&
          accountIndex < numAccountKeys - numReadonlyUnsignedAccounts);

      accounts.push({ pubkey, isSigner, isWritable });
    }

    // Instruction data
    const dataLen = decodeCompactU16(reader);
    const dataBytes = reader.readBytes(dataLen);
    const data = bytesToHex(dataBytes);

    instructions.push({ programId, accounts, data });
  }

  // Fee payer is always the first account key
  const feePayer = accountKeys[0];
  if (feePayer === undefined) {
    throw new Error('No account keys found in message');
  }

  return {
    feePayer,
    recentBlockhash,
    instructions,
    serializedMessage: serializedMessageHex,
  };
}

// ── compact-u16 decoder ────────────────────────────────────────────

/**
 * Decode a compact-u16 value (Solana's variable-length integer).
 * Inverse of encodeCompactU16 in serializer.ts.
 *
 * Encoding: Each byte uses 7 bits for data + bit 7 as continuation flag.
 * Max 3 bytes for values up to 0xFFFF.
 */
function decodeCompactU16(reader: BufferReader): number {
  let value = 0;
  let shift = 0;

  for (let i = 0; i < 3; i++) {
    const byte = reader.readUint8();
    value |= (byte & 0x7f) << shift;

    if ((byte & 0x80) === 0) {
      return value;
    }

    shift += 7;
  }

  throw new Error('compact-u16 too long (> 3 bytes)');
}

// ── BufferReader ───────────────────────────────────────────────────

/**
 * Sequential binary reader with cursor tracking.
 */
class BufferReader {
  private readonly bytes: Uint8Array;
  private offset: number;

  constructor(bytes: Uint8Array) {
    this.bytes = bytes;
    this.offset = 0;
  }

  readUint8(): number {
    const val = this.bytes[this.offset];
    if (val === undefined) throw new Error('Unexpected end of Solana message data');
    this.offset += 1;
    return val;
  }

  readBytes(count: number): Uint8Array {
    if (this.offset + count > this.bytes.length) {
      throw new Error(
        `Unexpected end of Solana message: need ${count} bytes at offset ${this.offset}, have ${this.bytes.length - this.offset}`,
      );
    }
    const slice = this.bytes.slice(this.offset, this.offset + count);
    this.offset += count;
    return slice;
  }
}
